import moment, { Moment } from "moment"
import React, { Fragment, useContext, useEffect, useRef, useState } from "react"
import { Loader } from "semantic-ui-react"
import api from "../../api"
import { STATUSES } from "~/constants"
import { CampProps, ReservationProps, SiteProps } from "../../interfaces"
import { ChevronRightIcon, PrinterIcon } from "@heroicons/react/24/solid"
import TodoDropdownButton from "../ui/TodoDropdownButton"
import { Disclosure, Menu, Transition } from "@headlessui/react"
import { AdminContext } from "~/contexts/AdminContext"
import ToDoItem from "../ToDos/ToDoItem"
import ToDosDatePicker from "./ToDosDatePicker"
import { useReactToPrint } from "react-to-print"
import Button from "../ui/Button"
import ParkingPass from "./ParkingPass"
import { useCamp } from "~/api2/hooks/useCamp"
import { useCampingStyles } from "~/api2/hooks/useCampingStyles"
import { useReservations } from "../Admin/Reservations/ReservationHooks/useReservations"
import { UpdateReservationProps } from "../Admin/Reservations/ReservationHooks/useReservation"
import { useQueryClient } from "@tanstack/react-query"
import { queryKeys } from "~/constants/queryKeys"
import { useDebouncedCallback } from "use-debounce"

interface IPrintOption {
  name: string
  toPrint: ReservationProps[]
  section?: "checkins" | "checkouts" | "current" | "parkings"
}

const ToDos = () => {
  const {
    index: { data: camp },
  } = useCamp()

  const {
    index: { data: campingStyles },
  } = useCampingStyles()

  const handleSetPage = (value) => {
    localStorage.setItem("page", value)
  }
  const handleSetReservation = (value) => {
    localStorage.setItem("selectedReservation", JSON.stringify(value))
  }
  const [selectedDate, setSelectedDate] = useState<Moment>(moment())
  const { data: reservations, isLoading: isLoadingReservations, refetch: refetchReservations } = useReservations({
    from: selectedDate.clone().startOf("day"),
    until: selectedDate.clone().endOf("day"),
    except_status: STATUSES.BLOCKED,
    include_recurrences: true,
  })
  const queryClient = useQueryClient()

  const [isLoading, setIsLoading] = useState(false)

  const [checkins, setCheckins] = useState<ReservationProps[]>([])
  const [parkings, setParkings] = useState<ReservationProps[]>([])
  const [checkouts, setCheckouts] = useState<ReservationProps[]>([])
  const [checkedOut, setCheckedOut] = useState<ReservationProps[]>([])
  const [current, setCurrent] = useState<ReservationProps[]>([])
  const [todosToPrint, setTodosToPrint] = useState<ReservationProps[]>([])
  const [todosTitle, setTodosTitle] = useState<string>("")
  const [selectedInterval, setSelectedInterval] = useState<string>("Today")

  const targetRef = useRef(null)

  const debouncedRefetchReservations = useDebouncedCallback(refetchReservations, 100)

  const countTotalGuests = (reservationList: ReservationProps[]): number => {
    return reservationList.reduce((sum, reservation) => {
      const adults = reservation.passengers || 0
      const kids = reservation.kid_campers || 0
      return sum + adults + kids
    }, 0)
  }
  const areGuestsShowing = campingStyles?.every((style: any) => {
    return style.prerequisites?.some((prereq: any) => {
      return prereq.kind === "passengers"
    })
  })

  const handleUpdateReservation = (resp: ReservationProps) => {
    queryClient.setQueryData(
      [queryKeys.RESERVATIONS, camp.id, selectedDate.clone().startOf("day"), selectedDate.clone().endOf("day")],
      (oldData: ReservationProps[]) => {
        const newData = oldData ? oldData.map((r) => (r.id === resp?.id ? resp : r)) : []
        return newData
      },
    )
  }

  useEffect(() => {
    if (!reservations) return

    setParkings(
      reservations.filter(
        (checkin) =>
          !checkin.is_checked_in &&
          moment(checkin.start_date).dayOfYear() == selectedDate.dayOfYear() &&
          !(checkin.kind == "recurring" && checkin.long_term_reservation?.kind == "utilities-only"),
      ),
    )
    setCheckins(
      reservations.filter(
        (checkin) =>
          !checkin.is_checked_in &&
          moment(checkin.start_date).dayOfYear() == selectedDate.dayOfYear() &&
          !(checkin.kind == "recurring" && checkin.long_term_reservation?.kind == "utilities-only"),
      ),
    )

    setCheckouts(
      reservations.filter(
        (checkout) =>
          !checkout.is_checked_out &&
          moment(checkout.end_date).dayOfYear() == selectedDate.dayOfYear() &&
          !(checkout.kind == "recurring" && checkout.long_term_reservation?.kind == "utilities-only") &&
          !(
            checkout.kind == "recurring" &&
            checkout.long_term_reservation &&
            checkout.long_term_reservation.reservations?.some((reservation) =>
              moment(reservation.end_date).isAfter(moment(checkout.end_date)),
            )
          ),
      ),
    )

    setCheckedOut(reservations.filter((checkout) => checkout.is_checked_out))

    setCurrent(
      reservations.filter(
        (checkin) =>
          (checkin.camper?.first_name ||
            checkin.camper?.last_name ||
            checkin.camper?.email ||
            checkin.camper?.phone_number) &&
          ((moment(checkin.start_date).dayOfYear() != selectedDate.dayOfYear() &&
            moment(checkin.end_date).dayOfYear() != selectedDate.dayOfYear()) ||
            (moment(checkin.start_date).dayOfYear() == selectedDate.dayOfYear() && checkin.is_checked_in)) &&
          !(checkin.kind == "recurring" && checkin.long_term_reservation?.kind == "utilities-only"),
      ),
    )
  }, [reservations])

  const usePrint = useReactToPrint({
    content: () => targetRef.current,
    onAfterPrint: () => {
      setTodosToPrint([])
      setIsLoading(false)
    },
  })

  useEffect(() => {
    if (todosToPrint?.length) {
      setIsLoading(true)
      const itemButtons: Element[] = targetRef.current.querySelectorAll(".print-hide")
      const itemChecbox: Element[] = targetRef.current.querySelectorAll(".checbox-show")
      const wrapItems: Element[] = targetRef.current.querySelectorAll(".print-wrap")

      itemButtons.forEach((item) => item.classList.add("hidden"))
      wrapItems.forEach((item) => item.classList.add("flex-wrap"))
      itemChecbox.forEach((item) => {
        item.classList.remove("hidden")
        item.classList.add("flex")
      })

      targetRef.current.style.display = "flex"
      usePrint()
      targetRef.current.style.display = "none"
    }
  }, [todosToPrint])

  const printOptions: IPrintOption[] = [
    {
      name: "Parking passes",
      toPrint: parkings,
      section: "parkings",
    },
    {
      name: "Checking in",
      toPrint: checkins,
      section: "checkins",
    },
    {
      name: "Checking out",
      toPrint: checkouts,
      section: "checkouts",
    },
    {
      name: "Checked out",
      toPrint: checkedOut,
      section: "checkouts",
    },
    {
      name: "Current guests",
      toPrint: current,
      section: "current",
    },
    {
      name: "All guests",
      toPrint: [...checkins, ...checkouts, ...checkedOut, ...current],
    },
  ]

  return (
    <div className="flex-grow sm:mx-4">
      {(isLoading || isLoadingReservations) && (
        <div className="fixed inset-0 bg-white/50 z-50">
          <Loader size="huge" active />
        </div>
      )}
      <div className="flex justify-between mr-4 sm:mr-0 flex-wrap gap-y-4 flex-col sm:flex-row">
        <div className="flex gap-1">
          <ToDosDatePicker
            selectedDate={selectedDate}
            visible={true}
            setSelectedDate={setSelectedDate}
            selectedInterval={selectedInterval}
            setSelectedInterval={setSelectedInterval}
          />
          <Menu as="div" className={"relative flex-shrink-0 ml-4"}>
            <Menu.Button className="h-full">
              <Button
                variant="none"
                className="h-full items-center flex-shrink-0 justify-self-end flex border border-gray-300 hover:bg-gray-50"
              >
                <PrinterIcon className="w-5 h-5 mr-2" />
                Print
              </Button>
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <div className="left-auto right-0 origin-top-right sm:right-auto sm:origin-top-left absolute sm:left-0 mt-2 w-72 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-40">
                <Menu.Items>
                  {printOptions.map((option) => (
                    <Menu.Item as="div">
                      <Button
                        variant="none"
                        className="flex items-center py-4 px-6 hover:bg-gray-100 cursor-pointer w-full text-gray-700"
                        onClick={() => {
                          setTodosToPrint(option.toPrint)
                          setTodosTitle(option.name)
                        }}
                        disabled={isLoading || isLoadingReservations || !option.toPrint?.length}
                      >
                        {option.name}
                      </Button>
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </div>
            </Transition>
          </Menu>
        </div>
        <TodoDropdownButton checkins={checkins} checkouts={checkouts} reservations={reservations} />
      </div>

      <div className="mx-auto w-full rounded-2xl bg-white mt-8  pb-12">
        <Disclosure defaultOpen={true} as="div" className="mt-8">
          {({ open }) => (
            <>
              <Disclosure.Button className="flex w-full justify-start">
                <ChevronRightIcon
                  className={`${open ? "rotate-90 transform" : ""} h-6 w-6 text-gray-700 mr-2 mt-0.5`}
                />
                <div className="text-xl mb-2 font-semibold">
                  Checking in today
                  <span className="ml-3.5 text-gray-500 font-normal text-base">
                    {checkins.length} {checkins.length === 1 ? "reservation" : "reservations"}
                    {areGuestsShowing &&
                      `, ${countTotalGuests(checkins)} ${countTotalGuests(checkins) === 1 ? "guest" : "guests"}`}
                  </span>
                </div>
              </Disclosure.Button>
              <Disclosure.Panel>
                {checkins.length == 0 ? (
                  <div className="italic">All guests are checked in today!</div>
                ) : (
                  <div className="bg-white shadow border border-gray-200 overflow-hidden sm:rounded-md">
                    <ul role="list" className="divide-y divide-gray-200" data-testid="checkins-list">
                      {checkins
                        .sort((a, b) => a.site.sort - b.site.sort)
                        .map((checkin) => (
                          <li key={checkin.id}>
                            <ToDoItem
                              setPage={handleSetPage}
                              setSelectedReservation={handleSetReservation}
                              checkin={checkin}
                              updateCallback={handleUpdateReservation}
                              campCurrency={camp.currency}
                              section="checkins"
                              refetchReservations={debouncedRefetchReservations}
                            />
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>

        <Disclosure defaultOpen={true} as="div" className="mt-8">
          {({ open }) => (
            <>
              <Disclosure.Button className="flex w-full justify-start">
                <ChevronRightIcon
                  className={`${open ? "rotate-90 transform" : ""} h-6 w-6 text-gray-700 mr-2 mt-0.5`}
                />
                <div className="text-xl mb-2 font-semibold">
                  Checking out today
                  <span className="ml-3.5 text-gray-500 font-normal text-base">
                    {checkouts.length} {checkouts.length === 1 ? "reservation" : "reservations"}
                    {areGuestsShowing &&
                      `, ${countTotalGuests(checkouts)} ${countTotalGuests(checkouts) === 1 ? "guest" : "guests"}`}
                  </span>
                </div>
              </Disclosure.Button>
              <Disclosure.Panel>
                {checkouts.length == 0 ? (
                  <div className="italic">All guests are checked out today!</div>
                ) : (
                  <div className="bg-white shadow border border-gray-200 overflow-hidden sm:rounded-md">
                    <ul role="list" className="divide-y divide-gray-200" data-testid="checking-out-list">
                      {checkouts
                        .sort((a, b) => a.site.sort - b.site.sort) // todo - fix this
                        .map((checkout) => (
                          <li key={checkout.id}>
                            <ToDoItem
                              setPage={handleSetPage}
                              setSelectedReservation={handleSetReservation}
                              checkin={checkout}
                              updateCallback={handleUpdateReservation}
                              campCurrency={camp.currency}
                              section="checkouts"
                              refetchReservations={debouncedRefetchReservations}
                            />
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>

        <Disclosure defaultOpen={true} as="div" className="mt-8">
          {({ open }) => (
            <>
              <Disclosure.Button className="flex w-full justify-start">
                <ChevronRightIcon
                  className={`${open ? "rotate-90 transform" : ""} h-6 w-6 text-gray-700 mr-2 mt-0.5`}
                />
                <div className="text-xl mb-2 font-semibold">
                  Checked out
                  <span className="ml-3.5 text-gray-500 font-normal text-base">
                    {checkedOut.length} {checkedOut.length === 1 ? "reservation" : "reservations"}
                    {areGuestsShowing &&
                      `, ${countTotalGuests(checkedOut)} ${countTotalGuests(checkedOut) === 1 ? "guest" : "guests"}`}
                  </span>
                </div>
              </Disclosure.Button>
              <Disclosure.Panel>
                {checkedOut.length == 0 ? (
                  <div className="italic">All guests are checked out!</div>
                ) : (
                  <div className="bg-white shadow border border-gray-200 overflow-hidden sm:rounded-md">
                    <ul role="list" className="divide-y divide-gray-200" data-testid="checked-out-list">
                      {checkedOut.map((checkin) => (
                        <li key={checkin.id}>
                          <ToDoItem
                            setPage={handleSetPage}
                            setSelectedReservation={handleSetReservation}
                            checkin={checkin}
                            updateCallback={handleUpdateReservation}
                            campCurrency={camp.currency}
                            section="checkouts"
                            refetchReservations={debouncedRefetchReservations}
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>

        <Disclosure defaultOpen={true} as="div" className="mt-8">
          {({ open }) => (
            <>
              <Disclosure.Button className="flex w-full justify-start">
                <ChevronRightIcon
                  className={`${open ? "rotate-90 transform" : ""} h-6 w-6 text-gray-700 mr-2 mt-0.5`}
                />
                <div className="text-xl mb-2 font-semibold">
                  Current guests
                  <span className="ml-3.5 text-gray-500 font-normal text-base">
                    {current.length} {current.length === 1 ? "reservation" : "reservations"}
                    {areGuestsShowing &&
                      `, ${countTotalGuests(current)} ${countTotalGuests(current) === 1 ? "guest" : "guests"}`}
                  </span>
                </div>
              </Disclosure.Button>
              <Disclosure.Panel>
                {current.length == 0 ? (
                  <div className="italic">No guests are staying today!</div>
                ) : (
                  <div className="bg-white shadow border border-gray-200 overflow-hidden sm:rounded-md">
                    <ul role="list" className="divide-y divide-gray-200" data-testid="current-guests-list">
                      {current
                        .sort((a, b) => a.site.sort - b.site.sort)
                        .map((checkin) => (
                          <li key={checkin.id}>
                            <ToDoItem
                              setPage={handleSetPage}
                              setSelectedReservation={handleSetReservation}
                              checkin={checkin}
                              updateCallback={handleUpdateReservation}
                              campCurrency={camp.currency}
                              section="current"
                              refetchReservations={debouncedRefetchReservations}
                            />
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      </div>

      <div className="hidden justify-center p-5 todos-print-margin" ref={targetRef}>
        <div className="flex flex-col items-center w-full">
          {printOptions.map((option, i) => {
            if (
              (todosTitle !== "All guests" && option.name !== todosTitle) ||
              (todosTitle === "All guests" && option.section === "parkings") ||
              !option.toPrint?.length
            )
              return

            return (
              <div className="w-full pagebreakafter">
                {option.section !== "parkings" && (
                  <div className="font-semibold text-3xl pb-5 text-center">
                    {option.name}{" "}
                    {["Current guests", "Check in", "Check out"].includes(option.name)
                      ? selectedDate.format("MM-DD-YYYY")
                      : ""}
                  </div>
                )}
                <div className="bg-white sm:rounded-md flex-1 w-full">
                  <ul role="list" className="w-full">
                    {option.toPrint
                      .sort((a, b) => a.site.sort - b.site.sort)
                      .map((checkin, idx) => {
                        return (
                          <>
                            {option.section === "parkings" ? (
                              <>
                                <ParkingPass reservation={checkin} camp={camp} />
                              </>
                            ) : (
                              <li key={checkin.id} className={`border border-gray-200 w-full-0`}>
                                <ToDoItem
                                  setPage={handleSetPage}
                                  setSelectedReservation={handleSetReservation}
                                  checkin={checkin}
                                  updateCallback={handleUpdateReservation}
                                  campCurrency={camp.currency}
                                  section={option.section}
                                  refetchReservations={debouncedRefetchReservations}
                                  forPrint
                                />
                              </li>
                            )}
                          </>
                        )
                      })}
                  </ul>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default ToDos
